import { useCallback, useEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'
import { tv } from 'tailwind-variants'

import { Icon } from '@/components/atoms/Icon'
import { Text } from '@/components/atoms/Text'
import { cn } from '@/utils/tailwind-merge'

export const dropdownMenuVariants = tv({
  slots: {
    containerSlot: 'relative inline-block',
    triggerSlot:
      'border-surface-white-300 bg-surface-white-50 text-surface-black-50 hover:bg-surface-white-100 active:bg-surface-white-200 flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm border',
    menuSlot:
      'border-surface-white-200 bg-surface-white-50 z-50 flex min-w-[200px] flex-col overflow-hidden rounded-lg border shadow-lg transition-all duration-150 ease-out',
    itemSlot:
      'text-body-md text-surface-black-50 hover:bg-surface-white-100 flex w-full cursor-pointer items-center gap-3 px-4 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
    dividerSlot: 'border-surface-white-200 my-1 border-t'
  }
})

export interface DropdownMenuItem {
  icon: string
  label: string
  onClick: () => void
  disabled?: boolean
  dividerAfter?: boolean
  variant?: 'default' | 'danger'
  testId?: string
}

export interface TDropdownMenuProps {
  items: DropdownMenuItem[]
  triggerIcon?: string
  align?: 'left' | 'right'
  testId?: string
  iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  triggerClassName?: string
}

export const DropdownMenu = ({
  items,
  triggerIcon = 'more_horiz',
  align = 'right',
  testId,
  iconSize = 'md',
  triggerClassName
}: TDropdownMenuProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const { containerSlot, triggerSlot, menuSlot, itemSlot } =
    dropdownMenuVariants()

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setMenuPosition({
      top: rect.bottom + 4,
      left: align === 'right' ? rect.right : rect.left
    })
  }, [align])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  useEffect(() => {
    if (menuRef.current) {
      if (isOpen) {
        menuRef.current.style.display = 'block'
        // Force reflow to trigger transition
        void menuRef.current.offsetHeight
        menuRef.current.style.opacity = '1'
        menuRef.current.style.transform = 'scale(1)'
      } else {
        menuRef.current.style.opacity = '0'
        menuRef.current.style.transform = 'scale(0.95)'
        // Hide after transition completes
        const timeout = setTimeout(() => {
          if (menuRef.current && !isOpen) {
            menuRef.current.style.display = 'none'
          }
        }, 150)
        return () => clearTimeout(timeout)
      }
    }
  }, [isOpen])

  const handleToggle = () => {
    if (!isOpen) {
      updatePosition()
    }
    setIsOpen(!isOpen)
  }

  const handleItemClick = (item: DropdownMenuItem) => {
    if (!item.disabled) {
      item.onClick()
      setIsOpen(false)
    }
  }

  const menu = (
    <div
      ref={menuRef}
      className={menuSlot()}
      style={{
        position: 'fixed',
        display: 'none',
        opacity: 0,
        transform: 'scale(0.95)',
        transformOrigin: align === 'right' ? 'top right' : 'top left',
        top: menuPosition.top,
        ...(align === 'right'
          ? { right: window.innerWidth - menuPosition.left }
          : { left: menuPosition.left })
      }}
      role='menu'
      aria-orientation='vertical'
      data-testid={testId ? `${testId}-menu` : undefined}
    >
      {items.map((item, index) => (
        <button
          key={`${item.label}-${index}`}
          type='button'
          className={cn(
            itemSlot(),
            item.dividerAfter && 'border-surface-white-200 border-b'
          )}
          onClick={() => handleItemClick(item)}
          disabled={item.disabled}
          role='menuitem'
          data-testid={
            item.testId
              ? testId
                ? `${testId}-${item.testId}`
                : item.testId
              : testId
              ? `${testId}-item-${index}`
              : undefined
          }
        >
          <Icon
            name={item.icon}
            size={iconSize}
            color={
              item.variant === 'danger' ? 'error-icon' : 'surface-black-50'
            }
          />
          <Text
            size='body-md'
            weight='regular'
            color={
              item.variant === 'danger' ? undefined : 'text-surface-white-400'
            }
            className={
              item.variant === 'danger' ? 'text-error-title' : undefined
            }
          >
            {item.label}
          </Text>
        </button>
      ))}
    </div>
  )

  return (
    <div className={containerSlot()} data-testid={testId}>
      <button
        ref={triggerRef}
        type='button'
        className={cn(triggerSlot(), triggerClassName)}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup='true'
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <Icon name={triggerIcon} size='sm' color='surface-black-50' />
      </button>
      {createPortal(menu, document.body)}
    </div>
  )
}
